// bd-deepcontext-b4zw — the create/edit job modal. ScheduledJobs.tsx carried two
// near-identical ~450-line copies of this form; the handful of real differences
// (title, submit label, blueprint ownJobId, the bd-bqw.1 auto-sync block and the
// date-field wording/required-ness) are driven by `mode`.
import React from "react";
import { useBackdropDismiss } from "../../hooks/useBackdropDismiss";
import CountryMultiSelect from "../CountryMultiSelect";
import PromptMultiSelect, { type BlueprintUsageMap } from "../PromptMultiSelect";
import type { SelectedDomainInfo } from "../SiteListModal";
import type { ScheduledJobFormData } from "../scheduledJobFormDefaults";
import DomainFiltersField from "./DomainFiltersField";
import PoolEstimatePanel from "./PoolEstimatePanel";
import SegmentedSwitch from "./SegmentedSwitch";
import type { JobFormMode } from "./formPayload";
import { MAX_URLS_PER_RUN } from "../scheduledJobRecommendation";
import { describeLocalModel } from "./localModelAccess";
import type { LocalModelServer, UrlPoolEstimate } from "./types";

interface ScheduledJobFormModalProps {
  mode: JobFormMode;
  /** Edit/duplicate mode only — names the job in the heading and scopes
   *  blueprint usage (and, in duplicate mode, the warn box below). */
  job?: { id: string; name: string };
  formData: ScheduledJobFormData;
  onChange: (patch: Partial<ScheduledJobFormData>) => void;
  onCountryCodesChange: (countryCodes: string[]) => void;
  countriesError: string | null;
  blueprintUsage: BlueprintUsageMap;
  /** Duplicate mode only — names the blueprint(s) being handed over in the warn box. */
  blueprintNames?: Record<string, string>;
  onSelectedBlueprintCountriesChange: (
    countriesByPromptId: Record<string, string | null | undefined>,
  ) => void;
  selectedDomainInfos: SelectedDomainInfo[];
  onOpenSiteListModal: () => void;
  poolEstimate: UrlPoolEstimate | null;
  estimateLoading: boolean;
  loading: boolean;
  /** deepcontext-7px2.4 — isLocalModelAllowed(user, impersonation). */
  showLocalModelPicker?: boolean;
  /** deepcontext-63k8.3 — configured self-hosted servers the picker offers. */
  localModels?: LocalModelServer[];
  onSubmit: (e: React.FormEvent) => void;
  onClose: () => void;
}

const runsPerDayInvalid = (runsPerDay: number) =>
  !Number.isFinite(runsPerDay) || runsPerDay < 1 || runsPerDay > 24;

// deepcontext-y74t — the backend rejects anything outside 1..MAX_URLS_PER_RUN
// ("Limit must be between 1 and 10000"), but the input only enforced min=1.
const urlsPerRunInvalid = (urlsPerRun: number) =>
  !Number.isFinite(urlsPerRun) || urlsPerRun < 1 || urlsPerRun > MAX_URLS_PER_RUN;

// deepcontext-c919.2 — "A" and "B" -> "A and B"; "A" alone -> "A".
const joinNames = (names: string[]): string =>
  names.length <= 1
    ? names[0] ?? ""
    : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;

const ScheduledJobFormModal: React.FC<ScheduledJobFormModalProps> = ({
  mode,
  job,
  formData,
  onChange,
  onCountryCodesChange,
  countriesError,
  blueprintUsage,
  blueprintNames,
  onSelectedBlueprintCountriesChange,
  selectedDomainInfos,
  onOpenSiteListModal,
  poolEstimate,
  estimateLoading,
  loading,
  showLocalModelPicker = false,
  localModels = [],
  onSubmit,
  onClose,
}) => {
  const isEdit = mode === "edit";
  const isDuplicate = mode === "duplicate";
  const pickedLocalModel = formData.localModelId
    ? describeLocalModel(formData.localModelId, localModels)
    : null;
  const unknownLocalModel =
    pickedLocalModel && !pickedLocalModel.configured ? pickedLocalModel : null;

  const backdropDismiss = useBackdropDismiss(onClose);

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      {...backdropDismiss}
    >
      {/* bd-deepcontext-zgbz — locked modal shell: a 16px-radius surface panel
          with a hairline border (mirrors ui/modal.tsx). */}
      <div
        role="dialog"
        aria-modal="true"
        className="bg-surface border border-line rounded-2xl shadow-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto"
      >
        <h3 className="text-lg font-semibold mb-4">
          {isDuplicate
            ? `Duplicate Job: ${job?.name}`
            : isEdit
              ? `Edit Job: ${job?.name}`
              : "Create Scheduled Job"}
        </h3>
        <form onSubmit={onSubmit} className="space-y-4">
          {isDuplicate && job && (
            <div className="rounded-lg border border-[#F1D78A] bg-warn-bg text-warn text-sm px-3 py-2.5">
              Saving will deactivate &quot;{job.name}&quot; and move the{" "}
              {joinNames(formData.promptIds.map((id) => blueprintNames?.[id] || id))}{" "}
              blueprint to this new job. A blueprint can be attached to one job only.
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-ink mb-1">
              Name
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => onChange({ name: e.target.value })}
              className="w-full px-3 py-2 border border-line rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
              required
              data-testid="scheduled-job-name-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-ink mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => onChange({ description: e.target.value })}
              className="w-full px-3 py-2 border border-line rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
              rows={2}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-ink mb-1">
              Brand Blueprints *
            </label>
            <PromptMultiSelect
              value={formData.promptIds}
              onChange={(promptIds) => onChange({ promptIds })}
              placeholder="Search and select brand blueprints..."
              required={true}
              blueprintUsage={blueprintUsage}
              ownJobId={isEdit || isDuplicate ? job?.id : undefined}
              onSelectedBlueprintCountriesChange={
                onSelectedBlueprintCountriesChange
              }
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-ink mb-1">
              Traffic from (countries) *
            </label>
            <CountryMultiSelect
              value={formData.countryCodes}
              onChange={onCountryCodesChange}
              placeholder="Search and select countries..."
            />
            {countriesError ? (
              <p className="text-xs text-risk mt-1">{countriesError}</p>
            ) : (
              <>
                <p className="text-xs text-ink-muted mt-1">
                  Restrict to URLs with observed traffic from the selected countries.
                </p>
              </>
            )}
          </div>

          {/* Unified job model (deepcontext-ia8xo.1): a single always-visible
              runs-per-day input (1–24, default 24 ≈ hourly) feeds the
              URL-pool recommendation below. */}
          <div>
            <label className="block text-sm font-medium text-ink mb-1">
              Runs per Day
            </label>
            <input
              type="number"
              value={formData.runsPerDay}
              onChange={(e) =>
                onChange({ runsPerDay: parseInt(e.target.value) })
              }
              className="w-full px-3 py-2 border border-line rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
              min="1"
              max="24"
              required
              data-testid="scheduled-job-runs-per-day-input"
            />
            <p className="text-xs text-ink-muted mt-1">
              How many times this job runs per day (1–24, evenly spaced).
            </p>
            {runsPerDayInvalid(formData.runsPerDay) && (
              <p className="text-xs text-risk mt-1">
                Runs per day must be between 1 and 24.
              </p>
            )}
          </div>

          <PoolEstimatePanel
            promptCount={formData.promptIds.length}
            urlsPerRun={formData.urlsPerRun}
            runsPerDay={formData.runsPerDay}
            poolEstimate={poolEstimate}
            estimateLoading={estimateLoading}
            onApplyRecommendation={(urlsPerRun) => onChange({ urlsPerRun })}
          />

          <div>
            <label className="block text-sm font-medium text-ink mb-1">
              URLs per Run
            </label>
            <input
              type="number"
              value={formData.urlsPerRun}
              onChange={(e) =>
                onChange({ urlsPerRun: parseInt(e.target.value) })
              }
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                urlsPerRunInvalid(formData.urlsPerRun)
                  ? "border-risk focus:ring-risk"
                  : "border-line focus:ring-accent"
              }`}
              min="1"
              max={MAX_URLS_PER_RUN}
              required
              data-testid="scheduled-job-urls-per-run-input"
            />
            <p className="text-xs text-ink-muted mt-1">
              Fixed number of URLs to process per run.
            </p>
            {urlsPerRunInvalid(formData.urlsPerRun) && (
              <p className="text-xs text-risk mt-1">
                URLs per run must be between 1 and 10,000.
              </p>
            )}
          </div>

          <div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.excludeHomepages}
                onChange={(e) => onChange({ excludeHomepages: e.target.checked })}
                className="h-4 w-4 rounded border-line text-accent focus:ring-accent"
              />
              <span className="text-sm font-medium text-ink">
                Exclude Homepages
              </span>
            </label>
            <p className="text-xs text-ink-muted mt-1">
              Filter out URLs that are homepages (/ or empty path)
            </p>
          </div>

          {/* bd-bqw.1 — auto-sync toggle. Always visible (even when current
              job has autoSyncManaged=false) so ops can re-enable. */}
          {isEdit && (
            <div
              data-testid="auto-sync-box"
              className="flex items-start gap-3 p-3 rounded-xl bg-nav-active"
            >
              <input
                id="auto-sync-managed"
                type="checkbox"
                aria-label="Auto-sync from DSP campaigns"
                checked={formData.autoSyncManaged}
                onChange={(e) => onChange({ autoSyncManaged: e.target.checked })}
                className="mt-1 accent-accent"
              />
              <label htmlFor="auto-sync-managed" className="text-sm text-ink">
                <span className="font-medium">Auto-sync from DSP campaigns</span>
                <p className="text-xs text-ink-muted mt-1">
                  When on, the job's end date and active status mirror the linked DSP campaigns (updated approximately every 3 hours). Manual edits to End Date or active toggle automatically turn this off.
                </p>
              </label>
            </div>
          )}

          {/* deepcontext-63k8.3 — per-job pick of the self-hosted server (or
              Gemini). A job still pointing at a server that is no longer
              configured keeps its id as an option; its runs fail with a clear
              reason instead of falling back to Gemini. */}
          {showLocalModelPicker && (
            <div
              data-testid="local-model-box"
              className="p-3 rounded-xl bg-nav-active"
            >
              <label
                htmlFor="local-model-id"
                className="block text-sm font-medium text-ink mb-1"
              >
                Model
              </label>
              <select
                id="local-model-id"
                data-testid="local-model-select"
                value={formData.localModelId ?? ""}
                onChange={(e) =>
                  onChange({ localModelId: e.target.value || null })
                }
                className="w-full px-3 py-2 border border-line rounded-lg bg-surface text-ink focus:outline-none focus:ring-2 focus:ring-accent"
              >
                <option value="">Default (Gemini)</option>
                {localModels.map((server) => (
                  <option key={server.id} value={server.id}>
                    {server.label}
                  </option>
                ))}
                {unknownLocalModel && (
                  <option value={formData.localModelId ?? ""}>
                    {unknownLocalModel.text}
                  </option>
                )}
              </select>
              <p className="text-xs text-ink-muted mt-1">
                Score this job's URLs on a self-hosted model instead of Gemini.
              </p>
              {unknownLocalModel && (
                <p
                  data-testid="local-model-not-configured"
                  className="text-xs text-warn mt-1"
                >
                  {formData.localModelId} is not configured on the server
                </p>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-ink mb-1">
                {isEdit ? (
                  <>Start Date {formData.startDate && "*"}</>
                ) : (
                  "Start Date *"
                )}
              </label>
              <input
                type="date"
                value={formData.startDate}
                onChange={(e) => onChange({ startDate: e.target.value })}
                className="w-full px-3 py-2 border border-line rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
                required={!isEdit}
                data-testid="scheduled-job-start-date-input"
              />
              <p className="text-xs text-ink-muted mt-1">
                {!isEdit || formData.startDate
                  ? "Job will not run before this date"
                  : "Optional (legacy jobs only)"}
              </p>
            </div>
            <div>
              <label className="flex items-center gap-1 text-sm font-medium text-ink mb-1">
                <span>
                  {isEdit ? (
                    <>End Date {formData.endDate && "*"}</>
                  ) : (
                    "End Date *"
                  )}
                </span>
                {isEdit && formData.autoSyncManaged && (
                  <span
                    title="End date is auto-synced from the linked DSP campaign(s) approximately every 3 hours, following the latest-ending campaign in both directions (it can move the end date earlier or later). The job stays active as long as at least one linked campaign is active. Manual edits override auto-sync — toggle 'auto-sync' on to re-enable."
                    aria-label="Auto-sync info"
                    className="inline-flex items-center justify-center w-4 h-4 rounded-full text-xs font-bold bg-nav-active text-accent-active cursor-help select-none"
                  >
                    i
                  </span>
                )}
              </label>
              <input
                type="date"
                value={formData.endDate}
                onChange={(e) => onChange({ endDate: e.target.value })}
                className="w-full px-3 py-2 border border-line rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
                required={!isEdit}
                data-testid="scheduled-job-end-date-input"
              />
              <p className="text-xs text-ink-muted mt-1">
                {isEdit && formData.autoSyncManaged
                  ? "Auto-synced from linked DSP campaigns. Manual edit will turn off auto-sync."
                  : !isEdit || formData.endDate
                  ? "Job will not run after this date"
                  : "Optional (legacy jobs only)"}
              </p>
            </div>
          </div>

          <DomainFiltersField
            selectedDomainInfos={selectedDomainInfos}
            onOpenSiteListModal={onOpenSiteListModal}
            domainExtensions={formData.domainExtensions}
            onDomainExtensionsChange={(domainExtensions) =>
              onChange({ domainExtensions })
            }
          />

          {/* bd-deepcontext-zgbz — URL Source and Country Distribution are
              SEGMENTED SWITCHES in the locked design, not full-width split
              buttons. Same two values and the same onChange as before. */}
          <div>
            <label className="block text-sm font-medium text-ink mb-1">
              URL Source
            </label>
            <SegmentedSwitch
              label="URL Source"
              value={formData.urlSourceMode}
              onChange={(urlSourceMode) => onChange({ urlSourceMode })}
              options={[
                { value: 'default', label: 'Default' },
                { value: 'classification', label: 'Classification' },
              ]}
            />
            <p className="text-xs text-ink-muted mt-1">
              {formData.urlSourceMode === 'classification'
                ? 'Uses URLs from classification results'
                : 'Uses URLs from RTB data feed'}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-ink mb-1">
              Country Distribution
            </label>
            <SegmentedSwitch
              label="Country Distribution"
              value={formData.countryDistributionMode}
              onChange={(countryDistributionMode) =>
                onChange({ countryDistributionMode })
              }
              options={[
                { value: 'proportional', label: 'Proportional' },
                { value: 'uniform', label: 'Uniform' },
              ]}
            />
            <p className="text-xs text-ink-muted mt-1">
              {formData.countryDistributionMode === 'proportional'
                ? 'Distributes URLs based on available pool size per country'
                : 'Distributes URLs evenly across countries'}
            </p>
          </div>

          {formData.urlSourceMode === 'classification' && (
          <div>
            <label className="block text-sm font-medium text-ink mb-1">
              Similarity Selectivity (%): {formData.similaritySelectivity}
            </label>
            <input
              type="range"
              min="0"
              max="100"
              step="5"
              value={formData.similaritySelectivity}
              onChange={(e) => onChange({ similaritySelectivity: parseInt(e.target.value) })}
              className="w-full h-2 bg-ghost-bg rounded-lg appearance-none cursor-pointer accent-accent"
            />
            <div className="flex justify-between text-xs text-ink-muted mt-1">
              <span>0 (no filtering)</span>
              <span>100 (only top matches)</span>
            </div>
            <p className="text-xs text-ink-muted mt-1">
              How selective to be relative to the best available match. 0 = no filtering, 100 = only top matches.
            </p>
          </div>
          )}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-ink-muted border border-line rounded-lg hover:bg-ghost-bg"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={
                loading ||
                runsPerDayInvalid(formData.runsPerDay) ||
                urlsPerRunInvalid(formData.urlsPerRun)
              }
              className="px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent-active disabled:opacity-50"
              data-testid="scheduled-job-submit"
            >
              {isEdit ? "Update Job" : "Create Job"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ScheduledJobFormModal;
